// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Lustre — the board.
//
// SELECT, THEN CONFIRM. Everything on screen is a target you tap to ARM, and one full-width bar at
// the bottom says exactly what committing will do and does it. That is the only model that fits: a
// phone has one thumb and this game has five verbs, and a game where the commit is a small button
// next to the thing you picked puts the decision under your hand.
//
// The bar's label is the whole rules explanation. "CUT Deep ▲ · 4t → +6 ✦ · dial +6" is the price,
// the reward and the consequence for everyone else, in one line, in the place you are already
// looking. Nothing about the price ladder needs to be read anywhere else.
//
// Layout is a fixed vertical budget with ONE flexible region. The Lode scrolls if it must and every
// other band is a fixed height, so the action bar can never be pushed off a short viewport — which
// is the failure that a screenshot at one width will not show you.

import { HUE_GLYPH, OPAL_GLYPH, TIERS } from './cards';
import {
  aArg, aVerb, actingSeat, bite, cutPrice, gMask, gRep, gTake2, gTake2Hue, handSize, isRough,
  legalActions, panHue, patronNeed, payFor, pips,
  roughCount, sOpal, sSlot, sTier, cFromShelf, cSlot, cTier, V_CLAIM, V_CUT, V_GATHER, V_PAN,
  V_PATRON, V_SHELVE, yieldOf, type State,
} from './rules';

export type Sel =
  | { kind: 'gather'; hues: number[] }
  | { kind: 'claim'; tier: number; slot: number; shelf: boolean }
  | { kind: 'shelve'; tier: number; slot: number; opal: boolean }
  | { kind: 'cut'; idx: number }
  | { kind: 'patron'; idx: number }
  | { kind: 'pan'; hues: number[] }
  | null;

export interface ViewHooks {
  onSelect: (sel: Sel) => void;
  onCommit: () => void;
  onHelp: () => void;
  onQuit: () => void;
  onRival: (seat: number) => void;
}

export interface Paint {
  state: State;
  mySeat: number;
  names: string[];
  sel: Sel;
  /** The packed action the current selection resolves to, or -1. */
  armed: number;
  /** Why the selection cannot be committed, in plain words. */
  reason: string;
  waiting: string;
  fault: string;
  /** Seats that have gone quiet. */
  away: Set<number>;
}

const el = (tag: string, cls?: string, text?: string): HTMLElement => {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
};

const esc = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);

export class View {
  readonly root: HTMLElement;
  private readonly wheel: HTMLElement;
  private readonly rivals: HTMLElement;
  private readonly lode: HTMLElement;
  private readonly patrons: HTMLElement;
  private readonly pool: HTMLElement;
  private readonly bench: HTMLElement;
  private readonly bar: HTMLButtonElement;
  private readonly note: HTMLElement;
  private last: Paint | null = null;

  constructor(private readonly hooks: ViewHooks) {
    this.root = el('div', 'game');
    const top = el('div', 'gtop');
    this.wheel = el('div', 'wheel');
    this.rivals = el('div', 'rivals');
    this.lode = el('div', 'lode');
    this.patrons = el('div', 'patrons');
    this.pool = el('div', 'pool');
    this.bench = el('div', 'bench');
    const barwrap = el('div', 'actbar');
    this.bar = el('button', 'commit') as HTMLButtonElement;
    this.bar.type = 'button';
    this.note = el('p', 'gnote');

    const tools = el('div', 'gtools');
    const help = el('button', 'gtool', '?') as HTMLButtonElement;
    help.type = 'button';
    help.setAttribute('aria-label', 'How to play');
    help.addEventListener('click', () => hooks.onHelp());
    const quit = el('button', 'gtool', '✕') as HTMLButtonElement;
    quit.type = 'button';
    quit.setAttribute('aria-label', 'Leave this round');
    quit.addEventListener('click', () => hooks.onQuit());
    tools.append(help, quit);

    this.bar.addEventListener('click', () => hooks.onCommit());
    barwrap.append(this.note, this.bar);
    top.append(this.wheel, this.rivals, this.lode, this.patrons, this.pool, this.bench);
    this.root.append(tools, top, barwrap);
  }

  destroy(): void {
    this.root.remove();
  }

  render(p: Paint): void {
    this.last = p;
    this.paintWheel(p);
    this.paintRivals(p);
    this.paintLode(p);
    this.paintPatrons(p);
    this.paintPool(p);
    this.paintBench(p);
    this.paintBar(p);
  }

  // ── the wheel band ──────────────────────────────────────────────────────────────
  private paintWheel(p: Paint): void {
    const s = p.state;
    const turn = actingSeat(s);
    const lanes = s.players
      .map((pl, seat) => {
        const pct = Math.min(100, (pl.pawn / s.close) * 100);
        return `<div class="lane${seat === p.mySeat ? ' me' : ''}${seat === turn ? ' on' : ''}">
          <i class="pawn" style="left:${pct}%"></i></div>`;
      })
      .join('');
    const upName = turn < 0 ? 'Done' : seat(p, turn);
    const left = s.players[turn] ? s.close - s.players[turn].pawn : 0;
    const price = s.mode.patronIsVerb ? 'ORDER' : 'CUT';
    this.wheel.innerHTML =
      `<div class="lanes">${lanes}</div>
       <div class="wrow"><span class="wup">${esc(upName)} · ${left} left</span>
       <span class="dial" title="the price of converting, and how far the table has pushed it">${price} ${cutPrice(s)}t · dial ${s.dial}</span></div>`;
  }

  // ── rivals ──────────────────────────────────────────────────────────────────────
  private paintRivals(p: Paint): void {
    const s = p.state;
    this.rivals.replaceChildren();
    for (let st = 0; st < s.n; st++) {
      if (st === p.mySeat) continue;
      const pl = s.players[st];
      const pip = pips(pl);
      let patron = 0;
      for (const id of pl.patrons) patron += s.patronDefs[id].value;
      const chip = el('button', `rchip${p.away.has(st) ? ' away' : ''}`) as HTMLButtonElement;
      chip.type = 'button';
      chip.innerHTML =
        `<span class="rname">${esc(seat(p, st))}</span><span class="rsc">${pl.lustre + patron}<i>✦</i></span>
         <span class="rpips">${pip.map((v, h) => `<i class="h${h}">${HUE_GLYPH[h]}${v}</i>`).join('')}</span>
         <span class="rrough">${roughCount(pl)}/${s.mode.roughCap}${p.away.has(st) ? ' · away' : ''}</span>`;
      chip.addEventListener('click', () => this.hooks.onRival(st));
      this.rivals.append(chip);
    }
  }

  // ── the Lode ────────────────────────────────────────────────────────────────────
  private paintLode(p: Paint): void {
    const s = p.state;
    const me = s.players[p.mySeat];
    const pip = pips(me);
    const legal = new Set(p.mySeat === actingSeat(s) ? legalActions(s) : []);
    this.lode.replaceChildren();

    for (let t = 2; t >= 0; t--) {
      const row = el('div', 'lrow');
      for (let k = 0; k < 3; k++) {
        const card = s.lode[t][k];
        if (!card) {
          row.append(el('div', 'card dark'));
          continue;
        }
        const pay = payFor(me, card, pip);
        const canClaim = [...legal].some((a) => aVerb(a) === V_CLAIM && !cFromShelf(aArg(a)) && cTier(aArg(a)) === t && cSlot(aArg(a)) === k);
        const canShelve = [...legal].some((a) => aVerb(a) === V_SHELVE && sTier(aArg(a)) === t && sSlot(aArg(a)) === k);
        const sel = p.sel;
        const armed =
          (sel?.kind === 'claim' && !sel.shelf && sel.tier === t && sel.slot === k) ||
          (sel?.kind === 'shelve' && sel.tier === t && sel.slot === k);
        const b = el('button', `card t${t}${armed ? ' armed' : ''}${canClaim ? ' afford' : pay ? ' near' : ''}`) as HTMLButtonElement;
        b.type = 'button';
        const pills = card.cost
          .map((need, h) => {
            if (need === 0) return '';
            const owe = Math.max(0, need - pip[h]);
            return `<i class="pill h${h}${owe === 0 ? ' free' : ''}">${HUE_GLYPH[h]}${owe}</i>`;
          })
          .join('');
        b.innerHTML =
          `<span class="cv">${s.mode.patronIsVerb ? '' : TIERS[t].cut}</span>
           <span class="chue h${card.hue}">${HUE_GLYPH[card.hue]}</span>
           <span class="cbite">+${TIERS[t].bite}</span>
           <span class="cpills">${pills}</span>`;
        b.setAttribute(
          'aria-label',
          `${TIERS[t].name} ${['Garnet', 'Topaz', 'Malachite', 'Azurite'][card.hue]}, worth ${TIERS[t].cut}`,
        );
        b.addEventListener('click', () => {
          if (canClaim) this.hooks.onSelect({ kind: 'claim', tier: t, slot: k, shelf: false });
          else if (canShelve) this.hooks.onSelect({ kind: 'shelve', tier: t, slot: k, opal: t > 0 && s.opalPool > 0 });
          else this.hooks.onSelect(null);
        });
        row.append(b);
      }
      this.lode.append(row);
    }
  }

  // ── Patrons ─────────────────────────────────────────────────────────────────────
  private paintPatrons(p: Paint): void {
    const s = p.state;
    const me = s.players[p.mySeat];
    const pip = pips(me);
    this.patrons.replaceChildren();
    // Ready orders first: if the rail ever has to scroll, what scrolls out of sight must be the
    // ones you cannot act on.
    const order = s.patronsUp
      .map((id, i) => ({ id, i }))
      .filter((x) => x.id >= 0)
      .sort((a, b) => rank(s, b.id, pip) - rank(s, a.id, pip));
    order.forEach(({ id, i }) => {
      const need = patronNeed(s, id);
      const def = s.patronDefs[id];
      const ready = need.every((v, h) => pip[h] >= v);
      const chip = el('button', `pchip${ready ? ' ready' : ''}${p.sel?.kind === 'patron' && p.sel.idx === i ? ' armed' : ''}`) as HTMLButtonElement;
      chip.type = 'button';
      chip.innerHTML =
        `<span class="pv">${def.value}<i>✦</i></span><span class="pneed">${need
          .map((v, h) => (v > 0 ? `<i class="h${h}">${HUE_GLYPH[h]}${Math.min(pip[h], v)}/${v}</i>` : ''))
          .join('')}</span>`;
      chip.addEventListener('click', () => this.hooks.onSelect(ready ? { kind: 'patron', idx: i } : null));
      this.patrons.append(chip);
    });
    if (this.patrons.childElementCount === 0) {
      this.patrons.append(el('p', 'pempty', 'Every order has been filled.'));
    }
  }

  // ── the grit pool ───────────────────────────────────────────────────────────────
  private paintPool(p: Paint): void {
    const s = p.state;
    const picked = p.sel?.kind === 'gather' ? p.sel.hues : p.sel?.kind === 'pan' ? p.sel.hues : [];
    const panning = p.sel?.kind === 'pan';
    this.pool.replaceChildren();
    for (let h = 0; h < 4; h++) {
      const took = picked.filter((x) => x === h).length;
      const b = el('button', `chip h${h}${took ? ' took' : ''}`) as HTMLButtonElement;
      b.type = 'button';
      b.innerHTML =
        `<span class="cg">${HUE_GLYPH[h]}</span><span class="cn">${panning ? s.tailings[h] : s.pool[h]}</span>` +
        (took ? `<i class="ctk">+${took}</i>` : '');
      b.setAttribute('aria-label', `${['Garnet', 'Topaz', 'Malachite', 'Azurite'][h]}, ${s.pool[h]} left`);
      b.addEventListener('click', () => this.hooks.onSelect(addHue(p.sel, h, panning)));
      this.pool.append(b);
    }
    const op = el('div', 'chip opal');
    op.innerHTML = `<span class="cg">${OPAL_GLYPH}</span><span class="cn">${s.opalPool}</span>`;
    op.setAttribute('aria-label', `Opal, ${s.opalPool} left`);
    this.pool.append(op);
    if (s.mode.pan) {
      const t = el('button', `chip tail${panning ? ' took' : ''}`) as HTMLButtonElement;
      t.type = 'button';
      // ≡ and not an APL quad: the first choice rendered as a tofu box on the deploy target, which
      // is invisible in a unit test and obvious the moment you look at the live page.
      t.innerHTML = `<span class="cg">≡</span><span class="cn">${s.tailings.reduce((a, b2) => a + b2, 0)}</span>`;
      t.setAttribute('aria-label', 'Pan the tailings');
      t.addEventListener('click', () => this.hooks.onSelect(panning ? null : { kind: 'pan', hues: [] }));
      this.pool.append(t);
    }
  }

  // ── your bench ──────────────────────────────────────────────────────────────────
  private paintBench(p: Paint): void {
    const s = p.state;
    const me = s.players[p.mySeat];
    const pip = pips(me);
    const rough = roughCount(me);
    const full = rough >= s.mode.roughCap;
    let patron = 0;
    for (const id of me.patrons) patron += s.patronDefs[id].value;

    const stat = `<div class="bstat"><b>${me.lustre + patron}<i>✦</i></b>
      <span>bite ${bite(me)} · yield ${yieldOf(bite(me))}</span>
      <span class="${full ? 'warn' : ''}">bench ${rough}/${s.mode.roughCap}</span>
      <span>${handSize(me)}/12 held</span></div>`;

    const spars = me.bench
      .map((sp, i) => {
        if (!isRough(sp)) return '';
        const armed = p.sel?.kind === 'cut' && p.sel.idx === i;
        return `<button type="button" class="spar h${sp.hue}${armed ? ' armed' : ''}" data-cut="${i}"
          aria-label="${TIERS[sp.tier].name} ${HUE_GLYPH[sp.hue]}, cut for ${TIERS[sp.tier].cut}">
          ${HUE_GLYPH[sp.hue]}<i>${s.mode.patronIsVerb ? TIERS[sp.tier].bite : TIERS[sp.tier].cut}</i></button>`;
      })
      .join('');
    const shelf = me.shelf
      .map((sp, i) => `<button type="button" class="spar shelf h${sp.hue}" data-shelf="${i}"
        aria-label="Shelved ${TIERS[sp.tier].name}">${HUE_GLYPH[sp.hue]}<i>↓</i></button>`)
      .join('');
    const held = `<div class="bheld">${[0, 1, 2, 3]
      .map((h) => (me.grit[h] > 0 ? `<i class="h${h}">${HUE_GLYPH[h]}${me.grit[h]}</i>` : ''))
      .join('')}${me.opals > 0 ? `<i class="opal">${OPAL_GLYPH}${me.opals}</i>` : ''}</div>`;

    this.bench.innerHTML = `${stat}<div class="brow">${spars}${shelf}</div>${held}
      <div class="bpip">${pip.map((v, h) => `<i class="h${h}">${HUE_GLYPH[h]}${v}</i>`).join('')}</div>`;

    this.bench.querySelectorAll<HTMLButtonElement>('[data-cut]').forEach((b) =>
      b.addEventListener('click', () => this.hooks.onSelect({ kind: 'cut', idx: Number(b.dataset.cut) })),
    );
    this.bench.querySelectorAll<HTMLButtonElement>('[data-shelf]').forEach((b) =>
      b.addEventListener('click', () =>
        this.hooks.onSelect({ kind: 'claim', tier: 0, slot: Number(b.dataset.shelf), shelf: true }),
      ),
    );
  }

  // ── the action bar ──────────────────────────────────────────────────────────────
  private paintBar(p: Paint): void {
    const mine = p.mySeat === actingSeat(p.state) && !p.state.over && !p.fault;
    this.bar.disabled = !mine || p.armed < 0;
    this.bar.textContent = p.fault
      ? p.fault
      : !mine
        ? p.waiting || 'Waiting…'
        : p.armed >= 0
          ? labelFor(p.state, p.mySeat, p.armed)
          : 'Pick something';
    this.note.textContent = p.reason;
    this.note.hidden = p.reason === '';
  }

  handleKey(e: KeyboardEvent): boolean {
    const p = this.last;
    if (!p) return false;
    if (e.key === 'Enter' || e.key === ' ') {
      if (!this.bar.disabled) this.hooks.onCommit();
      return true;
    }
    if (e.key === 'Escape') {
      this.hooks.onSelect(null);
      return true;
    }
    const n = '1234'.indexOf(e.key);
    if (n >= 0) {
      this.hooks.onSelect(addHue(p.sel, n, p.sel?.kind === 'pan'));
      return true;
    }
    return false;
  }
}

/** How close this order is to being takeable, so the rail can put the live ones first. */
function rank(s: State, id: number, pip: number[]): number {
  const need = patronNeed(s, id);
  let missing = 0;
  for (let h = 0; h < 4; h++) missing += Math.max(0, need[h] - pip[h]);
  return missing === 0 ? 100 + s.patronDefs[id].value : -missing;
}

function seat(p: Paint, i: number): string {
  return i === p.mySeat ? 'You' : (p.names[i] ?? `Seat ${i + 1}`);
}

/** Tapping a pool circle builds up a draw; tapping past what is legal starts again. */
function addHue(sel: Sel, hue: number, panning: boolean): Sel {
  if (panning && sel?.kind === 'pan') {
    return sel.hues.length >= 2 ? { kind: 'pan', hues: [hue] } : { kind: 'pan', hues: [...sel.hues, hue] };
  }
  if (sel?.kind === 'gather') {
    return sel.hues.length >= 5 ? { kind: 'gather', hues: [hue] } : { kind: 'gather', hues: [...sel.hues, hue] };
  }
  return { kind: 'gather', hues: [hue] };
}

/**
 * The label IS the rules explanation, so it states the whole consequence — what it costs in ticks,
 * what it pays, and what it does to everyone else's price.
 */
export function labelFor(s: State, mySeat: number, a: number): string {
  const me = s.players[mySeat];
  const arg = aArg(a);
  switch (aVerb(a)) {
    case V_GATHER: {
      if (gTake2(arg)) return `TAKE 2 ${HUE_GLYPH[gTake2Hue(arg)]} · 2t`;
      const took: string[] = [];
      for (let h = 0; h < 4; h++) if ((gMask(arg) >> h) & 1) took.push(HUE_GLYPH[h]);
      for (let i = 0; i < 2; i++) {
        const h = gRep(arg, i);
        if (h >= 0) took.push(HUE_GLYPH[h]);
      }
      return `GATHER ${took.join('')} · 2t`;
    }
    case V_CLAIM: {
      const card = cFromShelf(arg) ? me.shelf[cSlot(arg)] : s.lode[cTier(arg)][cSlot(arg)];
      if (!card) return 'CLAIM · 2t';
      const pay = payFor(me, card);
      const bits = pay
        ? [
            ...pay.grit.map((v, h) => (v > 0 ? `${v}${HUE_GLYPH[h]}` : '')).filter(Boolean),
            pay.opals > 0 ? `${pay.opals}${OPAL_GLYPH}` : '',
          ]
            .filter(Boolean)
            .join(' ')
        : '';
      return `CLAIM ${TIERS[card.tier].name} ${HUE_GLYPH[card.hue]} · ${bits} · 2t`;
    }
    case V_SHELVE: {
      const card = s.lode[sTier(arg)][sSlot(arg)];
      return `SHELVE ${card ? TIERS[card.tier].name : ''}${sOpal(arg) ? ` +1${OPAL_GLYPH}` : ''} · 2t`;
    }
    case V_CUT: {
      const sp = me.bench[arg];
      const v = sp ? TIERS[sp.tier].cut : 0;
      return `CUT ${sp ? TIERS[sp.tier].name : ''} ${sp ? HUE_GLYPH[sp.hue] : ''} · ${cutPrice(s)}t → +${v}✦ · dial +${v}`;
    }
    case V_PAN: {
      const hues: string[] = [];
      for (let i = 0; i < 2; i++) {
        const h = panHue(arg, i);
        if (h >= 0) hues.push(HUE_GLYPH[h]);
      }
      return `PAN ${hues.join('')} · 2t`;
    }
    case V_PATRON: {
      const id = s.patronsUp[arg];
      const v = id >= 0 ? s.patronDefs[id].value : 0;
      return `FILL ORDER · ${cutPrice(s)}t → +${v}✦ · burns 2 spars`;
    }
    default:
      return 'PASS';
  }
}
